import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {
  CREW_ROLES,
  PERMISSIONS,
  PILOT_ROLES,
  formatMinutes,
  localizedFlightTypeLabel,
  DEFAULT_HOME_BASE,
  minutesToDurationDigits,
  parseHoursMinutes,
  unrecordedFuelOil,
  type CrewRole,
  type FlightLog,
  type FlightLogAircraft,
  type FlightLogAuditEntry,
  type FlightLogInputDraft,
  type FlightType,
  type PilotRole,
} from '@jupi/shared'
import { ApiError, ConfirmDialog, RemoteContent, Title, formatDateTime, useAuth, useServerClock, useSnackbar } from '@jupi/ui'
import {
  useCrewMembers,
  useFlightLog,
  useFlightLogConfig,
  useFlightLogAircraft,
  useFlightLogMutations,
  useFlightTypes,
} from '../../api/flightLog'
import { FlightLogCard, clockOf, dateOf } from '../../components/flightLog/FlightLogCard'
import { FlightPlanReminder } from '../../components/flightLog/FlightPlanReminder'
import {
  FlightFieldsExtras,
  FuelOilConfirm,
  FlightFieldsMain,
  Section,
  dateIn,
  emptyFlightForm,
  readLastAircraft,
  rememberAircraft,
  useFlightModel,
  type FlightFieldsProps,
  type FlightFormState,
} from '../../components/flightLog/FlightFields'
import { useTimeBase } from '../../components/flightLog/timeBase'

// Logging a flight, built for a phone on the apron after shutdown: big
// touch targets, the aircraft and flight type as one tap each, native time
// pickers, steppers instead of number fields, and the flight's line in the
// log drawn live as the times go in. The times are in UTC or the club's
// zone, as the member chooses (remembered per browser, shared with the
// list). The optional parts (crew, night and IFR, fuel and oil, a total
// override) stay folded away until wanted. On a phone, a new flight opens
// with mik-ng's "close your flight plan" reminder. The save bar stays in
// reach above the bottom navigation.
//
// The same page shows an existing flight: editable while its pilot may
// still change it (or for a fleet manager), read-only otherwise, with the
// flight's audit trail underneath.

interface FormState extends FlightFormState {
  pilotUserId: string | null
  pilotRole: PilotRole
  crewUserId: string | null
  crewRole: CrewRole
  overrideTotal: boolean
  totalOverride: string
  totalOverrideReason: string
}

const emptyForm = (today: string): FormState => ({
  ...emptyFlightForm(today),
  pilotUserId: null,
  pilotRole: 'PIC',
  crewUserId: null,
  crewRole: 'instructor',
  overrideTotal: false,
  totalOverride: '',
  totalOverrideReason: '',
})

// The date and times of a flight as read in `timeZone`.
const clockFields = (flight: FlightLog, timeZone: string) => ({
  date: dateOf(flight.offBlockAt, timeZone),
  offBlockTime: clockOf(flight.offBlockAt, timeZone),
  takeoffTime: clockOf(flight.takeoffAt, timeZone),
  landingTime: clockOf(flight.landingAt, timeZone),
  onBlockTime: clockOf(flight.onBlockAt, timeZone),
})

const fromFlight = (flight: FlightLog, timeZone: string): FormState => ({
  registration: flight.registration,
  flightType: flight.flightType,
  ...clockFields(flight, timeZone),
  departure: flight.departure,
  arrival: flight.arrival,
  landings: flight.landings,
  personsOnBoard: flight.personsOnBoard,
  pilotUserId: flight.pilotUserId,
  pilotRole: flight.pilotRole,
  crewUserId: flight.crewUserId,
  crewRole: flight.crewRole ?? 'instructor',
  nightDigits: minutesToDurationDigits(flight.nightMinutes),
  nightLandings: flight.nightLandings,
  ifrDigits: minutesToDurationDigits(flight.ifrMinutes),
  // A saved flight is edited in litres, as it is stored.
  fuelAdded: flight.fuelAddedLitres?.toString() ?? '',
  fuelMeasured: flight.fuelMeasuredLitres?.toString() ?? '',
  fuelUnit: 'l',
  oilAdded: flight.oilAddedLitres?.toString() ?? '',
  oilUnit: 'l',
  fuelRemainingLitres: flight.fuelRemainingLitres,
  fuelStart: { litres: flight.fuelState?.startLitres ?? null },
  remarks: flight.remarks ?? '',
  overrideTotal: flight.totalOverrideMinutes != null,
  totalOverride: flight.totalOverrideMinutes != null ? formatMinutes(flight.totalOverrideMinutes) : '',
  totalOverrideReason: flight.totalOverrideReason ?? '',
})

export default function FlightLogFormPage() {
  const { flightId: raw } = useParams()
  const flightId = raw || undefined
  const { data, isLoading, error } = useFlightLog(flightId)
  const aircraftQuery = useFlightLogAircraft()
  const typesQuery = useFlightTypes()

  if (flightId && (isLoading || error || !data)) {
    return (
      <RemoteContent isLoading={isLoading} error={error}>
        {null}
      </RemoteContent>
    )
  }
  if (data && !data.flight.canEdit) return <FlightLogView flight={data.flight} audit={data.audit} />

  return (
    <RemoteContent isLoading={aircraftQuery.isLoading || typesQuery.isLoading} error={aircraftQuery.error ?? typesQuery.error}>
      {aircraftQuery.data && typesQuery.data ? (
        <FlightLogForm
          existing={data?.flight}
          audit={data?.audit ?? []}
          aircraft={aircraftQuery.data}
          flightTypes={typesQuery.data}
        />
      ) : null}
    </RemoteContent>
  )
}

function FlightLogForm({
  existing,
  audit,
  aircraft,
  flightTypes,
}: {
  existing?: FlightLog
  audit: FlightLogAuditEntry[]
  aircraft: FlightLogAircraft[]
  flightTypes: Pick<FlightType, 'name' | 'label' | 'labelSv' | 'labelEn'>[]
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, can } = useAuth()
  const { showSnackbar } = useSnackbar()
  const { create, update, remove } = useFlightLogMutations()
  const isManager = can(PERMISSIONS.flightLogManage)
  const { data: config } = useFlightLogConfig()
  const { timeBase, timeZone } = useTimeBase()
  const { utcMs } = useServerClock()
  // Today in whichever clock the member is logging in.
  const today = dateIn(utcMs, timeZone)

  const loggable = aircraft.filter((a) => a.current)
  const [form, setForm] = useState<FormState>(() => (existing ? fromFlight(existing, timeZone) : emptyForm(today)))

  // Switching clocks on a flight being corrected re-reads its stored times
  // in the new zone -- unless the member has already changed them, in which
  // case the switch says what the typed times mean.
  const previousZone = useRef(timeZone)
  useEffect(() => {
    const from = previousZone.current
    previousZone.current = timeZone
    if (from === timeZone) return
    if (!existing) {
      // A new flight dated "today" stays today in the other clock.
      const before = dateIn(Date.now(), from)
      setForm((f) => (f.date === before ? { ...f, date: dateIn(Date.now(), timeZone) } : f))
      return
    }
    setForm((f) => {
      const before = clockFields(existing, from)
      const untouched = (Object.keys(before) as (keyof typeof before)[]).every((k) => f[k] === before[k])
      return untouched ? { ...f, ...clockFields(existing, timeZone) } : f
    })
  }, [existing, timeZone])
  const [problem, setProblem] = useState<{ detail: string; conflicts?: { registration: string; offBlockAt: string; onBlockAt: string; pilotName: string }[] } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }))
  // The same, as the shared fields take it: for the keys they know.
  const setFlight: FlightFieldsProps<FormState>['set'] = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  const [crewOpen, setCrewOpen] = useState(Boolean(existing?.crewUserId) || (existing ? existing.pilotRole !== 'PIC' : false))
  const { data: members = [] } = useCrewMembers(crewOpen || isManager)
  const memberName = (userId: string | null) => members.find((m) => m.userId === userId)?.displayName ?? ''

  // A new flight starts on the aircraft this member flew last, from and
  // back to the club's home base (admin -> Settings).
  useEffect(() => {
    if (existing || form.registration) return
    const last = readLastAircraft()
    const pick = loggable.find((a) => a.registration === last) ?? loggable[0]
    if (pick) setForm((f) => ({ ...f, registration: pick.registration }))
  }, [existing, form.registration, loggable])
  const homeBase = config?.homeBase ?? DEFAULT_HOME_BASE
  useEffect(() => {
    if (!form.flightType && flightTypes.length === 1) set('flightType', flightTypes[0]!.name)
  }, [flightTypes, form.flightType])
  const arrival = form.arrival
  const model = useFlightModel(form, config, aircraft)
  const { selected, flightMinutes, nightMinutes, ifrMinutes, fuel, oil, measured } = model

  // The aircraft total this flight starts from, and where it leaves it.
  const totalBefore = existing ? existing.totalMinutesBefore : (selected?.current?.totalMinutes ?? null)
  const computedAfter = totalBefore != null && flightMinutes != null ? totalBefore + flightMinutes : null
  const overrideMinutes = form.overrideTotal ? parseHoursMinutes(form.totalOverride) : null
  const issues =
    form.overrideTotal && (overrideMinutes == null || form.totalOverrideReason.trim().length < 3)
      ? [...model.issues, 'override']
      : model.issues

  const pending = create.isPending || update.isPending
  const canSave = issues.length === 0 && !pending

  // Fuel and oil still empty: the pilot confirms none was added (or goes back).
  const [missingFuelOil, setMissingFuelOil] = useState<('fuel' | 'oil')[]>([])
  const [openFuelSignal, setOpenFuelSignal] = useState(0)

  const save = async (confirmedFuelOil = false) => {
    setProblem(null)
    const missing = unrecordedFuelOil(fuel, oil)
    if (missing.length && !confirmedFuelOil) {
      setMissingFuelOil(missing)
      return
    }
    const body: FlightLogInputDraft = {
      registration: form.registration,
      flightType: form.flightType,
      date: form.date,
      timeBase,
      offBlockTime: form.offBlockTime,
      takeoffTime: form.takeoffTime,
      landingTime: form.landingTime,
      onBlockTime: form.onBlockTime,
      departure: form.departure,
      arrival,
      landings: form.landings,
      personsOnBoard: form.personsOnBoard,
      ...(isManager && form.pilotUserId ? { pilotUserId: form.pilotUserId } : {}),
      pilotRole: form.pilotRole,
      crewUserId: form.crewUserId,
      crewRole: form.crewUserId ? form.crewRole : null,
      nightMinutes: nightMinutes ?? 0,
      nightLandings: form.nightLandings,
      ifrMinutes: ifrMinutes ?? 0,
      fuelAddedLitres: fuel ?? 0,
      fuelMeasuredLitres: measured ?? null,
      oilAddedLitres: oil ?? 0,
      fuelRemainingLitres: form.fuelRemainingLitres,
      remarks: form.remarks || null,
      totalOverrideMinutes: form.overrideTotal ? overrideMinutes : null,
      totalOverrideReason: form.overrideTotal ? form.totalOverrideReason : null,
    }
    try {
      if (existing) {
        await update.mutateAsync({ flightId: existing.flightId, body })
      } else {
        await create.mutateAsync(body)
        rememberAircraft(form.registration)
      }
      showSnackbar(t('flightLog.saved'))
      navigate('/fleet/logs')
    } catch (err) {
      if (err instanceof ApiError) {
        const extra = err.problem as unknown as { conflicts?: NonNullable<typeof problem>['conflicts'] } | null
        setProblem({ detail: err.message, conflicts: extra?.conflicts })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        showSnackbar(t('common.error'), { severity: 'error' })
      }
    }
  }

  const handleDelete = async () => {
    if (!existing) return
    try {
      await remove.mutateAsync(existing.flightId)
      showSnackbar(t('flightLog.deleted'))
      navigate('/fleet/logs')
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Box sx={{ pb: 12 }}>
      <Title label={existing ? t('flightLog.editFlight') : t('flightLog.newFlight')} />
      <Stack spacing={2}>
        {problem ? (
          <Alert severity="error" onClose={() => setProblem(null)}>
            {problem.detail}
            {problem.conflicts?.map((c) => (
              <Box key={`${c.registration}-${c.offBlockAt}`} sx={{ mt: 0.5 }}>
                {c.registration} {formatDateTime(c.offBlockAt)}–{clockOf(c.onBlockAt)} · {c.pilotName}
              </Box>
            ))}
          </Alert>
        ) : null}

        {loggable.length === 0 ? <Alert severity="warning">{t('flightLog.noJourneyLogs')}</Alert> : null}

        {/* Phones only: the pilot has just landed and is still at the field. */}
        {!existing ? (
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <FlightPlanReminder phone={config?.flightPlanPhone ?? ''} />
          </Box>
        ) : null}

        <FlightFieldsMain
          form={form}
          set={setFlight}
          setForm={setForm}
          model={model}
          aircraft={aircraft}
          flightTypes={flightTypes}
          today={today}
          retiredTypeLabel={
            existing
              ? localizedFlightTypeLabel(
                  { label: existing.flightTypeLabel, labelSv: existing.flightTypeLabelSv, labelEn: existing.flightTypeLabelEn },
                  i18n.language,
                )
              : undefined
          }
          defaultAirfield={existing ? null : homeBase}
        />

        <Accordion disableGutters variant="outlined" expanded={crewOpen} onChange={(_, open) => setCrewOpen(open)}>
          <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
            <Typography sx={{ fontWeight: 600 }}>{t('flightLog.crew')}</Typography>
            <Typography color="text.secondary" sx={{ ml: 1 }}>
              {t(`flightLog.pilotRole_${form.pilotRole}`)}
              {form.crewUserId ? ` + ${memberName(form.crewUserId)}` : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {isManager ? (
                <Autocomplete
                  options={members}
                  getOptionLabel={(m) => m.displayName}
                  value={members.find((m) => m.userId === (form.pilotUserId ?? existing?.pilotUserId ?? user?.id)) ?? null}
                  onChange={(_, m) => set('pilotUserId', m?.userId ?? null)}
                  isOptionEqualToValue={(a, b) => a.userId === b.userId}
                  renderInput={(params) => <TextField {...params} label={t('flightLog.pilot')} />}
                />
              ) : null}
              <TextField select label={t('flightLog.myRole')} value={form.pilotRole} onChange={(e) => set('pilotRole', e.target.value as PilotRole)}>
                {PILOT_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {t(`flightLog.pilotRole_${role}`)}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                options={members.filter((m) => m.userId !== (form.pilotUserId ?? existing?.pilotUserId ?? user?.id))}
                getOptionLabel={(m) => m.displayName}
                value={members.find((m) => m.userId === form.crewUserId) ?? null}
                onChange={(_, m) => {
                  set('crewUserId', m?.userId ?? null)
                  if (m && form.personsOnBoard < 2) set('personsOnBoard', 2)
                }}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                renderInput={(params) => <TextField {...params} label={t('flightLog.secondCrew')} />}
              />
              {form.crewUserId ? (
                <ToggleButtonGroup exclusive fullWidth value={form.crewRole} onChange={(_, v) => v && set('crewRole', v)}>
                  {CREW_ROLES.map((role) => (
                    <ToggleButton key={role} value={role}>
                      {t(`flightLog.crewRole_${role}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <FlightFieldsExtras form={form} set={setFlight} model={model} openFuelSignal={openFuelSignal} />

        <Section title={t('flightLog.aircraftTotal')}>
          {totalBefore != null ? (
            <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMinutes(totalBefore)} → <b>{computedAfter != null ? formatMinutes(computedAfter) : '–'}</b>
            </Typography>
          ) : (
            <Typography color="text.secondary">–</Typography>
          )}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={form.overrideTotal} onChange={(e) => set('overrideTotal', e.target.checked)} />}
            label={t('flightLog.overrideToggle')}
          />
          {form.overrideTotal ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('flightLog.overrideTotal')}
                placeholder={computedAfter != null ? formatMinutes(computedAfter) : '1234:56'}
                value={form.totalOverride}
                onChange={(e) => set('totalOverride', e.target.value)}
                error={Boolean(form.totalOverride) && overrideMinutes == null}
                helperText={
                  overrideMinutes != null && computedAfter != null
                    ? t('flightLog.overrideDifference', { difference: formatMinutes(overrideMinutes - computedAfter) })
                    : t('flightLog.overrideHelp')
                }
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                label={t('flightLog.overrideReason')}
                value={form.totalOverrideReason}
                onChange={(e) => set('totalOverrideReason', e.target.value)}
                multiline
                minRows={2}
              />
            </Stack>
          ) : null}
        </Section>

        <Section title={t('flightLog.remarks')}>
          <TextField fullWidth multiline minRows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} />
        </Section>

        {existing ? (
          <>
            <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)} startIcon={<Icon icon="mdi:delete-outline" />}>
              {t('flightLog.delete')}
            </Button>
            <AuditTrail audit={audit} />
          </>
        ) : null}
      </Stack>

      <ConfirmDialog
        open={confirmDelete}
        title={t('flightLog.delete')}
        message={t('flightLog.deleteConfirm')}
        confirmLabel={t('flightLog.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          setConfirmDelete(false)
          void handleDelete()
        }}
        onClose={() => setConfirmDelete(false)}
      />

      {/* Stays in reach above the phone's bottom bar. */}
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          left: { xs: 0, md: 240 },
          right: 0,
          bottom: { xs: 'calc(56px + env(safe-area-inset-bottom))', md: 0 },
          p: 1.5,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        <Button onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
        <Button variant="contained" size="large" disabled={!canSave} onClick={() => void save()} startIcon={<Icon icon="mdi:content-save-outline" />}>
          {t('flightLog.save')}
          {flightMinutes != null ? ` · ${formatMinutes(flightMinutes)}` : ''}
        </Button>
      </Paper>
      <FuelOilConfirm
        missing={missingFuelOil}
        onConfirm={() => {
          setMissingFuelOil([])
          void save(true)
        }}
        onBack={() => {
          setMissingFuelOil([])
          setOpenFuelSignal((n) => n + 1)
        }}
      />
    </Box>
  )
}

const AUDIT_ICON: Record<string, string> = {
  'flight_log.create': 'mdi:plus-circle-outline',
  'flight_log.update': 'mdi:pencil-outline',
  'flight_log.delete': 'mdi:delete-outline',
}

function AuditTrail({ audit }: { audit: FlightLogAuditEntry[] }) {
  const { t } = useTranslation()
  if (audit.length === 0) return null
  return (
    <Section title={t('flightLog.auditTrail')}>
      <Stack spacing={1.5}>
        {audit.map((entry) => {
          const changes = (entry.detail as { changes?: Record<string, [unknown, unknown]> } | null)?.changes
          return (
            <Box key={entry.auditId} sx={{ display: 'flex', gap: 1 }}>
              <Box component={Icon} icon={AUDIT_ICON[entry.action] ?? 'mdi:history'} width={20} sx={{ color: 'text.secondary', mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">
                  {t(`flightLog.audit_${entry.action.replace('flight_log.', '')}`)} · {entry.actorLabel ?? '–'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(entry.occurredAt)}
                </Typography>
                {changes
                  ? Object.entries(changes).map(([field, [before, after]]) => (
                      <Typography key={field} variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {field}: {String(before ?? '–')} → {String(after ?? '–')}
                      </Typography>
                    ))
                  : null}
              </Box>
            </Box>
          )
        })}
      </Stack>
    </Section>
  )
}

// A flight the caller may no longer change: the line as the log shows it,
// the details the card leaves out, and the audit trail.
function FlightLogView({ flight, audit }: { flight: FlightLog; audit: FlightLogAuditEntry[] }) {
  const { t } = useTranslation()
  const rows: [string, string | null][] = [
    [t('flightLog.pilot'), `${flight.pilotName} (${t(`flightLog.pilotRole_${flight.pilotRole}`)})`],
    [t('flightLog.secondCrew'), flight.crewName ? `${flight.crewName} (${t(`flightLog.crewRole_${flight.crewRole}`)})` : null],
    [t('flightLog.nightTime'), flight.nightMinutes ? formatMinutes(flight.nightMinutes) : null],
    [t('flightLog.nightLandings'), flight.nightLandings ? String(flight.nightLandings) : null],
    [t('flightLog.ifrTime'), flight.ifrMinutes ? formatMinutes(flight.ifrMinutes) : null],
    [t('flightLog.fuelAdded'), flight.fuelAddedLitres != null ? `${flight.fuelAddedLitres} l` : null],
    [t('flightLog.fuelMeasured'), flight.fuelMeasuredLitres != null ? `${flight.fuelMeasuredLitres} l` : null],
    [t('flightLog.fuelRemaining'), flight.fuelRemainingLitres != null ? `${flight.fuelRemainingLitres} l` : null],
    [t('flightLog.oilAdded'), flight.oilAddedLitres != null ? `${flight.oilAddedLitres} l` : null],
    [
      t('flightLog.aircraftTotal'),
      flight.totalMinutesBefore != null && flight.totalMinutesAfter != null
        ? `${formatMinutes(flight.totalMinutesBefore)} → ${formatMinutes(flight.totalMinutesAfter)}`
        : null,
    ],
    [t('flightLog.overrideReason'), flight.totalOverrideReason],
    [t('flightLog.journeyLogBook'), String(flight.bookNumber)],
    [t('flightLog.remarks'), flight.remarks],
  ]
  return (
    <Box>
      <Title label={t('flightLog.flight')} />
      <Stack spacing={2}>
        <FlightLogCard flight={flight} />
        <Alert severity="info">{t('flightLog.locked')}</Alert>
        <Section title={t('flightLog.details')}>
          <Stack spacing={0.5}>
            {rows
              .filter(([, value]) => value)
              .map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Section>
        <AuditTrail audit={audit} />
      </Stack>
    </Box>
  )
}
